import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import {
  getMyDaoMembership,
  selfRequestDaoMembership,
} from 'helpers/daoMember.api';
import { buttonStyles } from '../css/commonStyles';
import {
  memberCancelBtnSx,
  memberConfirmBtnSx,
  memberDialogBtnRowSx,
  memberDialogContentSx,
  memberDialogDescSx,
  memberDialogPaperSx,
  memberDialogTitleSx,
  memberDialogTitleWrapSx,
  memberFieldLabelSx,
  memberFieldsStackSx,
} from '../css/daoMemberModal.styles';
import {
  colors,
  disabledFieldSx,
  fieldBlockStyles,
  formFieldStyles,
} from '../css/revenueItems.styles';
import { formatRevenueSharingErrorMessage } from '../utils/revenueSharing.utils';
import { revenueSharingMessage } from '../utils/revenueSharingMessage';
import {
  connectMetaMaskWallet,
  getMetaMaskAccounts,
  formatWalletAddress,
} from '../utils/metamaskWallet.utils';

const membershipBannerBaseSx = {
  mb: 2.5,
  px: { xs: 2, md: 2.5 },
  py: 2,
  borderRadius: '12px',
  display: 'flex',
  alignItems: { xs: 'stretch', sm: 'center' },
  justifyContent: 'space-between',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: 2,
};

const requestBannerSx = {
  ...membershipBannerBaseSx,
  border: '1px solid rgba(0, 237, 195, 0.35)',
  backgroundColor: 'rgba(0, 237, 195, 0.08)',
};

const pendingBannerSx = {
  ...membershipBannerBaseSx,
  border: '1px solid rgba(245, 158, 11, 0.35)',
  backgroundColor: 'rgba(245, 158, 11, 0.08)',
};

const bannerTitleSx = {
  color: colors.textPrimary,
  fontFamily: 'Questrial, sans-serif',
  fontSize: '1.05rem',
  fontWeight: 600,
  lineHeight: 1.3,
  mb: 0.5,
};

const bannerBodySx = {
  color: colors.textSecondary,
  fontFamily: 'Questrial, sans-serif',
  fontSize: '0.9rem',
  lineHeight: 1.5,
};

const requestCtaBtnSx = {
  ...buttonStyles,
  flexShrink: 0,
  alignSelf: { xs: 'stretch', sm: 'center' },
  minHeight: 40,
  px: 2.5,
  borderRadius: '10px',
  textTransform: 'none',
  fontFamily: 'Questrial, sans-serif',
  fontSize: '0.95rem',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    ...buttonStyles['&:hover'],
    boxShadow: 'none',
  },
};

const REUSABLE_STATUSES = new Set(['rejected', 'revoked', 'declined']);

const getProfileDisplayName = (user) => {
  if (!user) return '';
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return fullName || user.name || user.fullname || '';
};

const DaoMembershipBanner = ({ currentDao, user }) => {
  const daoId = currentDao?._id;
  const [loading, setLoading] = useState(false);
  const [membership, setMembership] = useState(null);
  const [metaMaskWallet, setMetaMaskWallet] = useState('');
  const [allowSelfJoinRequest, setAllowSelfJoinRequest] = useState(!!currentDao?.allowSelfJoinRequest);
  const [requestOpen, setRequestOpen] = useState(false);
  const [form, setForm] = useState({ displayName: '', note: '' });
  const [acting, setActing] = useState(false);
  const [connectingWallet, setConnectingWallet] = useState(false);

  const connectedWallet = metaMaskWallet || '';
  const status = membership?.status || null;
  const profileDisplayName = useMemo(() => getProfileDisplayName(user), [user]);

  const refreshMetaMask = useCallback(async () => {
    try {
      if (!window.ethereum) {
        setMetaMaskWallet('');
        return;
      }
      const accounts = await getMetaMaskAccounts();
      setMetaMaskWallet(accounts?.[0] ? String(accounts[0]).trim().toLowerCase() : '');
    } catch (err) {
      setMetaMaskWallet('');
    }
  }, []);

  const load = useCallback(async () => {
    if (!daoId) return;
    try {
      setLoading(true);
      const res = await getMyDaoMembership(daoId);
      setMembership(res?.data || null);
      setAllowSelfJoinRequest(!!res?.dao?.allowSelfJoinRequest || !!currentDao?.allowSelfJoinRequest);
    } catch (error) {
      // Unauthenticated / missing profile should not block the page.
      setMembership(null);
    } finally {
      setLoading(false);
    }
  }, [daoId, currentDao?.allowSelfJoinRequest]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    refreshMetaMask();
    if (!window.ethereum?.on) return undefined;
    const handleAccountsChanged = (accounts) => {
      setMetaMaskWallet(accounts?.[0] ? String(accounts[0]).trim().toLowerCase() : '');
    };
    window.ethereum.on('accountsChanged', handleAccountsChanged);
    return () => {
      if (window.ethereum?.removeListener) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      }
    };
  }, [refreshMetaMask]);

  const handleConnectMetaMask = async () => {
    try {
      setConnectingWallet(true);
      const address = await connectMetaMaskWallet();
      setMetaMaskWallet(address);
      revenueSharingMessage.success('MetaMask wallet connected.');
    } catch (error) {
      if (error?.code === 'METAMASK_MISSING') {
        revenueSharingMessage.error('Install MetaMask to connect your wallet.');
      } else if (error?.code === 4001) {
        revenueSharingMessage.error('MetaMask connection was cancelled.');
      } else {
        revenueSharingMessage.error(error?.message || 'Failed to connect MetaMask.');
      }
    } finally {
      setConnectingWallet(false);
    }
  };

  const handleSelfRequest = async () => {
    try {
      setActing(true);
      let wallet = connectedWallet;
      if (!wallet) {
        wallet = await connectMetaMaskWallet();
        setMetaMaskWallet(wallet);
      }
      await selfRequestDaoMembership(
        daoId,
        {
          displayName: form.displayName.trim() || undefined,
          note: form.note.trim() || undefined,
        },
        wallet,
      );
      revenueSharingMessage.success('Join request submitted.');
      setRequestOpen(false);
      await load();
    } catch (error) {
      if (error?.code === 'METAMASK_MISSING') {
        revenueSharingMessage.error('Install MetaMask to connect your wallet.');
      } else {
        revenueSharingMessage.error(formatRevenueSharingErrorMessage(error, 'Failed to submit join request.'));
      }
    } finally {
      setActing(false);
    }
  };

  const openRequestDialog = () => {
    setForm({ displayName: profileDisplayName, note: '' });
    setRequestOpen(true);
  };

  const renderRequestDialog = () => (
    <Dialog
      open={requestOpen}
      onClose={() => !acting && setRequestOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: memberDialogPaperSx }}
    >
      <DialogTitle sx={memberDialogTitleWrapSx}>
        <Typography component="span" sx={memberDialogTitleSx}>
          Request to join DAO
        </Typography>
      </DialogTitle>
      <DialogContent sx={memberDialogContentSx}>
        <Typography sx={memberDialogDescSx}>
          Submit a membership request for the organizer to review.
        </Typography>
        <Box sx={memberFieldsStackSx}>
          <Box sx={fieldBlockStyles}>
            <Typography sx={memberFieldLabelSx}>Linked wallet</Typography>
            <TextField
              fullWidth
              disabled
              value={connectedWallet ? formatWalletAddress(connectedWallet) : 'Not connected'}
              sx={disabledFieldSx}
            />
            {!connectedWallet ? (
              <Button
                disabled={connectingWallet || acting}
                onClick={handleConnectMetaMask}
                sx={{ color: colors.accent, textTransform: 'none', px: 0, alignSelf: 'flex-start' }}
              >
                {connectingWallet ? (
                  <CircularProgress size={16} sx={{ color: colors.accent }} />
                ) : (
                  'Connect MetaMask'
                )}
              </Button>
            ) : (
              <Typography sx={{ color: colors.textSecondary, fontSize: '0.8rem' }}>
                To use a different wallet, switch the active account in MetaMask.
              </Typography>
            )}
          </Box>
          <Box sx={fieldBlockStyles}>
            <Typography sx={memberFieldLabelSx}>Display name (optional)</Typography>
            <TextField
              fullWidth
              value={form.displayName}
              onChange={(e) => {
                const displayName = e.target.value;
                setForm((prev) => ({ ...prev, displayName }));
              }}
              sx={formFieldStyles}
            />
          </Box>
          <Box sx={fieldBlockStyles}>
            <Typography sx={memberFieldLabelSx}>Note to organizer (optional)</Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={form.note}
              onChange={(e) => {
                const note = e.target.value;
                setForm((prev) => ({ ...prev, note }));
              }}
              sx={formFieldStyles}
            />
          </Box>
        </Box>
        <Box sx={memberDialogBtnRowSx}>
          <Button disabled={acting} onClick={() => setRequestOpen(false)} sx={memberCancelBtnSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={acting || connectingWallet}
            onClick={handleSelfRequest}
            sx={memberConfirmBtnSx}
          >
            {acting ? <CircularProgress size={20} sx={{ color: '#1a1d23' }} /> : 'Submit request'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );

  const renderRequestCta = () => (
    <>
      <Box sx={requestBannerSx}>
        <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
          <Typography sx={bannerTitleSx}>Request to join this DAO</Typography>
          <Typography sx={bannerBodySx}>Ask the organizer to approve you as a member.</Typography>
        </Box>
        <Button variant="contained" sx={requestCtaBtnSx} onClick={openRequestDialog}>
          Request to join
        </Button>
      </Box>
      {renderRequestDialog()}
    </>
  );

  // Avoid layout flash for approved / no-banner users.
  if (loading) return null;

  // Approved members: no banner — outcomes are communicated by email.
  if (status === 'approved') return null;

  if (status === 'pending_review') {
    return (
      <Box sx={pendingBannerSx}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={bannerTitleSx}>Membership pending review</Typography>
          <Typography sx={bannerBodySx}>
            An organizer will review your request. You will be notified by email when a decision is made.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (status === 'invited') {
    return (
      <Box sx={requestBannerSx}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={bannerTitleSx}>You have a pending invitation</Typography>
          <Typography sx={bannerBodySx}>
            Open the invitation link from your email to accept and continue.
          </Typography>
        </Box>
      </Box>
    );
  }

  // rejected / declined / revoked / no record: show self-request CTA when enabled.
  // Approve/reject results are email-only — do not show status banners here.
  const canSelfRequest =
    allowSelfJoinRequest && (!status || REUSABLE_STATUSES.has(status));

  if (!canSelfRequest) return null;

  return renderRequestCta();
};

const mapStateToProps = (state) => ({
  user: state.user || null,
});

export default connect(mapStateToProps)(DaoMembershipBanner);
